import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import useAiChatConversation from '../hooks/useAiChatConversation';
import useAiChatToolActions from '../hooks/useAiChatToolActions';
import AiChatComposer from './AiChatComposer';
import AiChatMessage from './AiChatMessage';

import type { ReactElement } from 'react';
import type IAiChatConversation from '../interfaces/IAiChatConversation';

import styles from '../sass/AiChat.module.scss';

/** Distance from the bottom, in pixels, within which the view follows new content. */
const PIN_THRESHOLD = 48;

/**
 * One conversation: its messages, and the box to write the next one.
 *
 * Holds one `useChat` for its whole life. The parent mounts it once the stored rows are on hand
 * and keys it on the conversation, so a move to another chat mounts a fresh one and the adoption
 * of a created chat does not. Returns a fragment: the parent owns the frame.
 */
function AiChatConversation({
  chatId, initialMessages, chatContext, screen, hasLoadError, onChatCreated, sendRef,
}: IAiChatConversation): ReactElement {
  const { t } = useTranslation();
  const onToolOutput = useAiChatToolActions();
  const {
    messages, isStreaming, isSending, streamError, send,
  } = useAiChatConversation({
    chatId, initialMessages, chatContext, onChatCreated, onToolOutput,
  });

  useEffect(() => {
    if (!sendRef) {
      return undefined;
    }
    // Parent dialog calls send for Create company; ref is an imperative handle.
    // eslint-disable-next-line no-param-reassign -- MutableRefObject.current
    sendRef.current = send;
    return () => {
      // eslint-disable-next-line no-param-reassign -- MutableRefObject.current
      sendRef.current = null;
    };
  }, [send, sendRef]);

  const listRef = useRef<HTMLDivElement>(null);
  // Follows new content only while the reader is at the bottom, so scrolling back is not undone
  // by the next chunk of a reply.
  const isPinnedRef = useRef<boolean>(true);

  const handleScroll = () => {
    const list = listRef.current;

    if (list) {
      isPinnedRef.current = list.scrollHeight - list.scrollTop - list.clientHeight < PIN_THRESHOLD;
    }
  };

  useEffect(() => {
    const list = listRef.current;

    if (list && isPinnedRef.current) {
      list.scrollTop = list.scrollHeight;
    }
  }, [messages]);

  return (
    <>
      <div className={styles.messages} ref={listRef} onScroll={handleScroll}>
        {hasLoadError ? <p className={styles.error}>{t('aiChat.loadFailed')}</p> : null}

        {!messages.length ? (
          <p className={styles.note}>{chatId ? t('aiChat.empty') : t('aiChat.emptyNewChat')}</p>
        ) : null}

        {messages.map((message, index) => (
          <AiChatMessage
            key={message.id}
            message={message}
            // Only the reply being written now carries the typing dots, and that is the last thing
            // in the list. The flag on a message the user wrote is ignored by the component.
            isStreaming={Boolean(isStreaming && index === messages.length - 1)}
            isLatest={index === messages.length - 1}
          />
        ))}

        {streamError ? (
          <p className={styles.error}>{t('aiChat.sendFailed', { message: streamError })}</p>
        ) : null}
      </div>

      <AiChatComposer
        onSend={send}
        isDisabled={isSending}
        isStreaming={isStreaming}
        screen={screen}
      />
    </>
  );
}

export default AiChatConversation;
